"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import Image from "next/image";
import { useParams, useSearchParams } from "next/navigation";
import { ClinicCarousel } from "@/app/[tenant]/self-schedule/components/clinic-carousel";
import { AuthSurface } from "@/components/auth";
import { MfaDialog } from "@/components/mfa";
import { PatientProfileBadge } from "@/components/patient/patient-profile-badge";
import { PurchaseAuthProvider } from "@/contexts/purchase-auth-provider";
import { ShopCatalogProvider } from "@/contexts/shop-catalog-provider";
import { useShopDisplaySettings } from "@/contexts/shop-display-settings-provider";
import { useSignOutPatient } from "@/hooks/patient-claim/use-sign-out-patient";
import { useMfaPurchaseAuth } from "@/hooks/shop/use-mfa-purchase-auth";
import { useShopCart } from "@/hooks/shop/use-shop-cart";
import { parseShopSort } from "@/lib/shop-catalog-view";
import { SHOP_CATEGORY_QUERY_PARAM, SHOP_CHECKOUT_QUERY_PARAM, SHOP_ITEM_QUERY_PARAM, SHOP_SECTION_QUERY_PARAM, SHOP_SORT_QUERY_PARAM, resolveShopLinkTargets } from "@/lib/shop-links";
import { ShopFulfillmentMethod } from "@/types/enums/shop-fulfillment-method";
import { ShopPromotionAnnouncementBar } from "./components/promotion-announcement-bar";
import PurchaseFlow from "./purchase-flow";
import { selfScheduleQueryOptions } from "@/api/check-in/self-schedule/self-schedule-query";
const ShopPageInner: React.FC<{
  tenantSlug: string;
}> = t0 => {
  const $ = _c(48);
  const {
    tenantSlug
  } = t0;
  const auth = useMfaPurchaseAuth();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = resolveShopLinkTargets(searchParams?.get(SHOP_SECTION_QUERY_PARAM) ?? null, searchParams?.get(SHOP_ITEM_QUERY_PARAM) ?? null);
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    initialSection,
    featuredItemId
  } = t1;
  let t2;
  if ($[2] !== searchParams) {
    t2 = () => ({
      category: searchParams?.get(SHOP_CATEGORY_QUERY_PARAM) ?? null,
      sort: parseShopSort(searchParams?.get(SHOP_SORT_QUERY_PARAM))
    });
    $[2] = searchParams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [initialView] = useState(t2);
  let t3;
  if ($[4] !== searchParams) {
    t3 = () => searchParams?.get(SHOP_CHECKOUT_QUERY_PARAM) === "1";
    $[4] = searchParams;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [startAtCheckout] = useState(t3);
  const {
    bannerImageUrl,
    clinicName
  } = useShopDisplaySettings();
  const handleSignOut = useSignOutPatient();
  const cart = useShopCart(tenantSlug);
  const [mfaOpen, setMfaOpen] = useState(false);
  const [deliveryChoice, setDeliveryChoice] = useState(null);
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setMfaOpen(true);
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== auth) {
    t5 = {
      ...auth,
      ensureAuthenticated: t4
    };
    $[7] = auth;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const wrappedAuth = t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      ...selfScheduleQueryOptions.frontPage(),
      select: _temp
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    data: clinicHeader
  } = useQuery(t6);
  const t7 = deliveryChoice === ShopFulfillmentMethod.PICKUP;
  let t8;
  if ($[10] !== cart.lines || $[11] !== t7) {
    t8 = <ShopPromotionAnnouncementBar lines={cart.lines} isPickup={t7} />;
    $[10] = cart.lines;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const t9 = clinicName ? `${clinicName} shop banner` : "Shop banner";
  const t10 = Boolean(bannerImageUrl);
  let t11;
  if ($[13] !== bannerImageUrl || $[14] !== t10 || $[15] !== t9) {
    t11 = <ClinicCarousel overrideImageUrl={bannerImageUrl} overrideImageAlt={t9} bannerAspect={true} forceExpanded={t10} className="h-36 opacity-90 shadow-md md:mx-auto md:h-48 md:max-w-4xl" />;
    $[13] = bannerImageUrl;
    $[14] = t10;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== auth.buyer || $[18] !== handleSignOut) {
    t12 = auth.buyer && <PatientProfileBadge firstName={auth.buyer.firstName} lastName={auth.buyer.lastName} onSignOut={handleSignOut} />;
    $[17] = auth.buyer;
    $[18] = handleSignOut;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== clinicHeader) {
    t13 = clinicHeader?.logoUrl && <Image src={clinicHeader.logoUrl} alt={clinicHeader.tenantName ?? "Clinic logo"} width={100} height={60} className="z-10 h-9 w-auto object-contain opacity-90 transition-all duration-300 ease-in-out" />;
    $[20] = clinicHeader;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t12 || $[23] !== t13) {
    t14 = <div className="mx-auto flex max-w-4xl items-center justify-end px-4 md:px-0"><div className="flex items-end gap-3 mt-1 md:mb-4 md:mt-4">{t12}{t13}</div></div>;
    $[22] = t12;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== cart || $[26] !== initialSection || $[27] !== startAtCheckout || $[28] !== tenantSlug) {
    t15 = <main className="mx-auto w-full px-4 md:max-w-7xl"><PurchaseFlow tenantSlug={tenantSlug} initialSection={initialSection} cart={cart} startAtCheckout={startAtCheckout} onDeliveryChoice={setDeliveryChoice} /></main>;
    $[25] = cart;
    $[26] = initialSection;
    $[27] = startAtCheckout;
    $[28] = tenantSlug;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t11 || $[31] !== t14 || $[32] !== t15) {
    t16 = <div className="relative min-h-0 flex-1 pb-16 md:overflow-y-auto">{t11}{t14}{t15}</div>;
    $[30] = t11;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t16 || $[35] !== t8) {
    t17 = <div className="flex min-h-screen flex-col md:h-full md:min-h-0">{t8}{t16}</div>;
    $[34] = t16;
    $[35] = t8;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== featuredItemId || $[38] !== initialView || $[39] !== t17 || $[40] !== tenantSlug) {
    t18 = <ShopCatalogProvider tenantSlug={tenantSlug} featuredItemId={featuredItemId} initialView={initialView}>{t17}</ShopCatalogProvider>;
    $[37] = featuredItemId;
    $[38] = initialView;
    $[39] = t17;
    $[40] = tenantSlug;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== mfaOpen) {
    t19 = <MfaDialog open={mfaOpen} onOpenChange={setMfaOpen} surface={AuthSurface.SHOP} tAndSFooterText="completing this purchase" />;
    $[42] = mfaOpen;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t18 || $[45] !== t19 || $[46] !== wrappedAuth) {
    t20 = <PurchaseAuthProvider value={wrappedAuth}>{t18}{t19}</PurchaseAuthProvider>;
    $[44] = t18;
    $[45] = t19;
    $[46] = wrappedAuth;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  return t20;
};
export default function ShopPage() {
  const $ = _c(2);
  const params = useParams();
  const tenantSlug = params?.tenant ?? "";
  let t0;
  if ($[0] !== tenantSlug) {
    t0 = <ShopPageInner tenantSlug={tenantSlug} />;
    $[0] = tenantSlug;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function _temp(data) {
  return {
    logoUrl: data?.logoUrl,
    tenantName: data?.tenantName
  };
}